import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { usePaywall } from "../components/PaywallModal";
import {
  Zap,
  ArrowLeft,
  FileText,
  Target,
  Sparkles,
  AlertTriangle,
  Loader2,
  Briefcase,
  Lock,
  CheckCircle2,
  CheckCircle,
  XCircle,
  ArrowRight,
  Download,
  TrendingUp,
  AlertCircle,
  Star,
  Lightbulb,
  Shield,
  BookOpen,
  FileCheck,
  Info,
  RefreshCw,
  Eye
} from "lucide-react";
import ReactMarkdown from "react-markdown";

const ResumeOptimization = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { user } = useAuth();
  const { showPaywall } = usePaywall();
  const reportRef = useRef(null);
  
  const [suggestions, setSuggestions] = useState(null);
  const [parsedReport, setParsedReport] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
    } else {
      setLoading(false);
    }
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [jobId]);

  // Parse markdown suggestions into structured format
  useEffect(() => {
    if (suggestions) {
      const parsed = parseReport(suggestions);
      setParsedReport(parsed);
    }
  }, [suggestions]);

  const parseReport = (markdown) => {
    const sections = {
      matchScore: null,
      strengths: [],
      improvements: [],
      missingSkills: [],
      keywordSuggestions: [],
      summary: ""
    };

    // Extract match score (look for percentage)
    const scoreMatch = markdown.match(/(\d{1,3})%?\s*(match|score|compatibility)/i) ||
                       markdown.match(/(match|score|compatibility)[:\s]*(\d{1,3})%?/i);
    if (scoreMatch) {
      sections.matchScore = parseInt(scoreMatch[1] || scoreMatch[2]);
    }

    // Split by headings
    const lines = markdown.split('\n');
    let currentSection = 'summary';
    
    lines.forEach(line => {
      const lowerLine = line.toLowerCase();
      
      if (lowerLine.includes('strength') || lowerLine.includes('what you have') || lowerLine.includes('✓') || lowerLine.includes('positive')) {
        currentSection = 'strengths';
      } else if (lowerLine.includes('improvement') || lowerLine.includes('suggestion') || lowerLine.includes('recommend') || lowerLine.includes('consider')) {
        currentSection = 'improvements';
      } else if (lowerLine.includes('missing skill') || lowerLine.includes('skill gap') || lowerLine.includes('skills to add') || lowerLine.includes('skills to learn')) {
        currentSection = 'missingSkills';
      } else if (lowerLine.includes('keyword') || lowerLine.includes('ats') || lowerLine.includes('term')) {
        currentSection = 'keywordSuggestions';
      }

      // Extract list items
      const bulletMatch = line.match(/^[\s]*[-•*]\s*(.+)/);
      const numberedMatch = line.match(/^[\s]*\d+[.)]\s*(.+)/);
      const item = bulletMatch ? bulletMatch[1] : numberedMatch ? numberedMatch[1] : null;

      if (item && item.trim()) {
        if (currentSection === 'strengths') {
          sections.strengths.push(item.trim());
        } else if (currentSection === 'improvements') {
          sections.improvements.push(item.trim());
        } else if (currentSection === 'missingSkills') {
          sections.missingSkills.push(item.trim());
        } else if (currentSection === 'keywordSuggestions') {
          sections.keywordSuggestions.push(item.trim());
        }
      }
    });

    // If no structured data found, use the raw markdown
    if (sections.strengths.length === 0 && sections.improvements.length === 0) {
      sections.summary = markdown;
    }

    return sections;
  };

  const downloadPDF = async () => {
    if (!suggestions || !job) return;
    
    setDownloading(true);
    try {
      const response = await fetch(`${API}/reports/resume-optimization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          job_id: jobId,
          job_title: job.title,
          company: job.company || "Company",
          suggestions: suggestions
        }),
        credentials: "include",
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Resume_Optimization_${job.title.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Report downloaded successfully!");
      } else {
        toast.error("Failed to download report");
      }
    } catch (err) {
      toast.error("Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const fetchJobDetails = async () => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setJob(data);
      } else {
        setError("Job not found");
      }
    } catch (err) {
      setError("Failed to load job details");
    } finally {
      setLoading(false);
    }
  };

  const generateSuggestions = async () => {
    if (!jobId) {
      toast.error("Please select a job first");
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setGenerating(true);
    setError(null);
    setNeedsUpgrade(false);

    const timeoutId = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }, 60000); // 60 seconds timeout

    try {
      const response = await fetch(`${API}/ai/resume-optimization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_id: jobId }),
        credentials: "include",
        signal: abortControllerRef.current.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions);
        toast.success("Resume optimization suggestions generated!");
      } else if (response.status === 403 || response.status === 402) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.detail?.paywall) {
          showPaywall(errorData.detail);
        } else if (errorData.detail?.includes?.("Pro tier") || errorData.detail?.includes?.("upgrade")) {
          setNeedsUpgrade(true);
        } else {
          setError(errorData.detail || "Access denied");
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || "Failed to generate suggestions");
      }
    } catch (err) {
      if (err.name === "AbortError") {
        setError("Request timed out. Please try again.");
      } else {
        setError("Failed to generate suggestions. Please try again.");
      }
    } finally {
      setGenerating(false);
    }
  };

  // Redirect if not a job seeker
  if (user?.role !== "jobseeker") {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-crimson mb-2">Job Seekers Only</h1>
          <p className="text-gray-400 mb-6">This feature is available for job seekers</p>
          <Button onClick={() => navigate(-1)} className="bg-crimson hover:bg-crimson-hover">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">
            RESUME <span className="text-crimson">OPTIMIZATION</span>
          </h1>
          <p className="text-gray-400 text-sm">
            Get AI-powered suggestions to improve your resume for specific jobs
          </p>
        </div>

        {/* Upgrade Banner */}
        {needsUpgrade && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-crimson/20 to-purple-500/20 border border-crimson/50 p-6 mb-8"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-crimson/20 flex items-center justify-center shrink-0">
                <Sparkles className="w-6 h-6 text-crimson" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-bold mb-2">Upgrade to Pro</h3>
                <p className="text-gray-400 mb-4">
                  Resume optimization is a Pro feature. Upgrade to get personalized suggestions 
                  for improving your resume based on specific job requirements.
                </p>
                <Button
                  onClick={() => navigate("/subscription")}
                  className="bg-crimson hover:bg-crimson-hover"
                  data-testid="upgrade-btn"
                >
                  Upgrade Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Job Selection / Details */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-crimson animate-spin" />
          </div>
        ) : !jobId ? (
          <div className="bg-obsidian-light border border-[#222] p-8 text-center">
            <Briefcase className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">Select a Job</h2>
            <p className="text-gray-400 mb-6">
              Browse jobs and click "Optimize Resume" to get personalized suggestions
            </p>
            <Button
              onClick={() => navigate("/jobs")}
              className="bg-crimson hover:bg-crimson-hover"
            >
              Browse Jobs
            </Button>
          </div>
        ) : (
          <>
            {/* Selected Job Card */}
            {job && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-obsidian-light border border-[#222] p-6 mb-6"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs uppercase tracking-wider text-crimson mb-2 block">
                      Optimizing for
                    </span>
                    <h2 className="font-heading text-xl font-bold mb-2">{job.title}</h2>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{job.industry}</span>
                      <span>{job.location}</span>
                      <span>{job.seniority_level}</span>
                    </div>
                    {job.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {job.skills.map((skill, idx) => (
                          <span key={idx} className="px-2 py-1 bg-[#1a1a1a] text-xs text-gray-300">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={generateSuggestions}
                    disabled={generating || needsUpgrade}
                    className="bg-crimson hover:bg-crimson-hover shrink-0"
                    data-testid="generate-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Suggestions
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Error Message */}
            {error && !needsUpgrade && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-500/10 border border-red-500/30 p-6 mb-6"
              >
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  <div>
                    <h3 className="font-bold text-red-400">Error</h3>
                    <p className="text-gray-400">{error}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Suggestions */}
            {suggestions && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                ref={reportRef}
              >
                {/* Report Header */}
                <div className="bg-gradient-to-r from-crimson/20 to-purple-500/10 border border-crimson/30 p-6 mb-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-crimson/20 flex items-center justify-center">
                        <FileText className="w-7 h-7 text-crimson" />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-bold">Resume Optimization Report</h3>
                        <p className="text-gray-400">
                          For: <span className="text-white">{job?.title}</span>
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={downloadPDF}
                      disabled={downloading}
                      className="bg-crimson hover:bg-crimson-hover"
                      data-testid="download-report-btn"
                    >
                      {downloading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download PDF
                    </Button>
                  </div>
                </div>

                {/* Match Score */}
                {parsedReport?.matchScore && (
                  <div className="bg-obsidian-light border border-[#222] p-6 mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-heading text-lg font-bold mb-1">Overall Match Score</h4>
                        <p className="text-gray-500 text-sm">How well your profile matches this job</p>
                      </div>
                      <div className={`text-4xl font-bold ${
                        parsedReport.matchScore >= 80 ? "text-green-400" :
                        parsedReport.matchScore >= 60 ? "text-yellow-400" :
                        "text-orange-400"
                      }`}>
                        {parsedReport.matchScore}%
                      </div>
                    </div>
                    <div className="mt-4 h-3 bg-[#1a1a1a] rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${parsedReport.matchScore}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className={`h-full ${
                          parsedReport.matchScore >= 80 ? "bg-green-500" :
                          parsedReport.matchScore >= 60 ? "bg-yellow-500" :
                          "bg-orange-500"
                        }`}
                      />
                    </div>
                  </div>
                )}

                {/* Structured Report Sections */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Strengths */}
                  {parsedReport?.strengths?.length > 0 && (
                    <div className="bg-obsidian-light border border-green-500/30 p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <h4 className="font-heading font-bold text-green-400">Your Strengths</h4>
                          <p className="text-gray-500 text-xs">What matches this job</p>
                        </div>
                      </div>
                      <ul className="space-y-2">
                        {parsedReport.strengths.slice(0, 6).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Improvements */}
                  {parsedReport?.improvements?.length > 0 && (
                    <div className="bg-obsidian-light border border-yellow-500/30 p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-yellow-500/20 flex items-center justify-center">
                          <Lightbulb className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                          <h4 className="font-heading font-bold text-yellow-400">Suggestions</h4>
                          <p className="text-gray-500 text-xs">Ways to improve your profile</p>
                        </div>
                      </div>
                      <ul className="space-y-2">
                        {parsedReport.improvements.slice(0, 6).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <ArrowRight className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Skills & Keywords */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Missing Skills */}
                  {parsedReport?.missingSkills?.length > 0 && (
                    <div className="bg-obsidian-light border border-orange-500/30 p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-orange-500/20 flex items-center justify-center">
                          <AlertCircle className="w-5 h-5 text-orange-400" />
                        </div>
                        <div>
                          <h4 className="font-heading font-bold text-orange-400">Skills to Add</h4>
                          <p className="text-gray-500 text-xs">Consider adding these skills</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {parsedReport.missingSkills.slice(0, 10).map((skill, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Keywords */}
                  {parsedReport?.keywordSuggestions?.length > 0 && (
                    <div className="bg-obsidian-light border border-blue-500/30 p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="font-heading font-bold text-blue-400">Keywords to Include</h4>
                          <p className="text-gray-500 text-xs">ATS-friendly terms</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {parsedReport.keywordSuggestions.slice(0, 10).map((keyword, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-sm"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Full Report (Fallback or Additional Details) */}
                {(parsedReport?.summary || (!parsedReport?.strengths?.length && !parsedReport?.improvements?.length)) && (
                  <div className="bg-obsidian-light border border-[#222] p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-purple-500/20 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-purple-400" />
                      </div>
                      <h4 className="font-heading font-bold">Detailed Analysis</h4>
                    </div>
                    <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-white prose-h3:text-lg prose-h3:text-crimson prose-p:text-gray-300 prose-li:text-gray-300 prose-strong:text-white">
                      <ReactMarkdown>{parsedReport?.summary || suggestions}</ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
                  <p className="text-gray-500 text-sm">
                    Apply these suggestions to improve your chances of getting hired
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={downloadPDF}
                      disabled={downloading}
                      variant="outline"
                      className="border-crimson text-crimson hover:bg-crimson hover:text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Report
                    </Button>
                    <Button
                      onClick={() => navigate("/profile/build")}
                      className="bg-crimson hover:bg-crimson-hover"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Update Profile
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Initial State - No Suggestions Yet */}
            {!suggestions && !generating && !error && !needsUpgrade && (
              <div className="bg-obsidian-light border border-[#222] p-8 text-center">
                <Target className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">Ready to Optimize</h3>
                <p className="text-gray-400 mb-6">
                  Click "Generate Suggestions" to get AI-powered recommendations 
                  for optimizing your resume for this specific job.
                </p>
              </div>
            )}
          </>
        )}

        {/* How It Works */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-obsidian-light border border-[#222] p-4">
            <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center mb-3">
              <span className="text-crimson font-bold">1</span>
            </div>
            <h4 className="font-bold mb-1">Select a Job</h4>
            <p className="text-gray-500 text-sm">
              Choose a job posting you're interested in
            </p>
          </div>
          <div className="bg-obsidian-light border border-[#222] p-4">
            <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center mb-3">
              <span className="text-crimson font-bold">2</span>
            </div>
            <h4 className="font-bold mb-1">AI Analysis</h4>
            <p className="text-gray-500 text-sm">
              Our AI compares your profile to job requirements
            </p>
          </div>
          <div className="bg-obsidian-light border border-[#222] p-4">
            <div className="w-8 h-8 bg-crimson/20 flex items-center justify-center mb-3">
              <span className="text-crimson font-bold">3</span>
            </div>
            <h4 className="font-bold mb-1">Get Suggestions</h4>
            <p className="text-gray-500 text-sm">
              Receive specific improvements to boost your chances
            </p>
          </div>
        </div>

        {/* ATS-Optimized Resume Section */}
        <ATSResumeSection />
      </main>
    </div>
  );
};

// ATS Resume Section Component
const ATSResumeSection = () => {
  const [atsResume, setAtsResume] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for cached ATS resume on load
    fetchCachedResume();
  }, []);

  const fetchCachedResume = async () => {
    try {
      const response = await fetch(`${API}/ai/generate-ats-resume`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.cached) {
          setAtsResume(data);
        }
      }
    } catch (error) {
      // Silently fail - user hasn't generated one yet
    }
  };

  const generateATSResume = async () => {
    setGenerating(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/ai/generate-ats-resume`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        const data = await response.json();
        setAtsResume(data);
        toast.success("ATS-optimized resume generated!");
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Failed to generate resume");
        toast.error(errorData.detail || "Failed to generate resume");
      }
    } catch (error) {
      setError("Network error. Please try again.");
      toast.error("Network error. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const downloadATSResume = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${API}/reports/ats-resume/pdf`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "ATS_Optimized_Resume.pdf";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Resume downloaded!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Download failed");
      }
    } catch (error) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <motion.div
      className="mt-10 pt-8 border-t border-[#333]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
          <FileCheck className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold">ATS-OPTIMIZED RESUME</h2>
          <p className="text-gray-500 text-sm">Generate an ATS-compliant resume from your profile</p>
        </div>
      </div>

      <div className="bg-obsidian-light border border-[#222] p-6">
        {/* Info */}
        <div className="flex items-start gap-3 mb-6 p-4 bg-blue-500/10 border border-blue-500/30">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-400 text-sm font-medium mb-1">What is ATS?</p>
            <p className="text-gray-400 text-sm">
              Applicant Tracking Systems (ATS) scan resumes before human recruiters see them. 
              An ATS-optimized resume uses the right keywords, formatting, and structure to 
              pass these automated filters and increase your chances of getting interviews.
            </p>
          </div>
        </div>

        {!atsResume ? (
          <div className="text-center py-8">
            <FileCheck className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold mb-2">Generate Your ATS Resume</h3>
            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
              Our AI will transform your profile into a professionally formatted, 
              keyword-optimized resume that passes ATS filters.
            </p>
            
            <Button
              onClick={generateATSResume}
              disabled={generating}
              className="bg-green-600 hover:bg-green-700"
              data-testid="generate-ats-resume-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate ATS Resume
                </>
              )}
            </Button>
            
            {error && (
              <p className="text-red-400 text-sm mt-4">{error}</p>
            )}
          </div>
        ) : (
          <div>
            {/* ATS Score */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  atsResume.ats_score >= 80 ? 'bg-green-500/20' :
                  atsResume.ats_score >= 60 ? 'bg-yellow-500/20' :
                  'bg-red-500/20'
                }`}>
                  <span className={`text-2xl font-bold ${
                    atsResume.ats_score >= 80 ? 'text-green-400' :
                    atsResume.ats_score >= 60 ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {atsResume.ats_score}%
                  </span>
                </div>
                <div>
                  <p className="font-medium text-white">ATS Compatibility Score</p>
                  <p className="text-gray-500 text-sm">
                    {atsResume.ats_score >= 80 ? 'Excellent - Ready for submission!' :
                     atsResume.ats_score >= 60 ? 'Good - Minor improvements possible' :
                     'Needs work - Consider updating your profile'}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={generateATSResume}
                  disabled={generating}
                  variant="outline"
                  className="border-[#333] text-white hover:border-green-500"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={downloadATSResume}
                  disabled={downloading}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="download-ats-resume-btn"
                >
                  {downloading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download PDF
                </Button>
              </div>
            </div>

            {/* Improvements Applied */}
            {atsResume.improvements_applied?.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-white mb-3">Optimizations Applied:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {atsResume.improvements_applied.map((improvement, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-gray-300">{improvement}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resume Preview */}
            <div className="bg-obsidian border border-[#333] p-6 max-h-96 overflow-y-auto">
              <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Resume Preview
              </h4>
              <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap font-mono text-xs text-gray-300 leading-relaxed">
                {atsResume.resume_content}
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ResumeOptimization;
